import React, { useEffect } from "react";
import { InstrumentTypeResponse } from "../../codegen-api";
import { AssetResponse } from "../../utils/asset";
import { ISectionData } from "./CommandModal.types";
import { swrUpdateMillis } from "./CommandModal.helpers";

type HighlightedMarketIndex = {
  sectionIndex: number;
  dataIndex: number;
};

// Toggle the menu when ⌘K is pressed
export function useCmdKToggle(
  setShowCommandModal: React.Dispatch<React.SetStateAction<boolean>>
) {
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      // Handle CMD+K
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setShowCommandModal((o) => !o);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [setShowCommandModal]);
}

// Handle navigation while the modal is open
export function useCommandKeyboardNav(
  showCommandModal: boolean,
  highlightedMarketIndex: HighlightedMarketIndex,
  setHighlightedMarketIndex: React.Dispatch<
    React.SetStateAction<HighlightedMarketIndex>
  >,
  sectionsData: ISectionData[],
  onRowClick: (
    asset: AssetResponse,
    derivative: InstrumentTypeResponse
  ) => void
) {
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (showCommandModal && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
        if (!highlightedMarketIndex) {
          setHighlightedMarketIndex({
            sectionIndex: 0,
            dataIndex: 0,
          });
          return;
        }

        const [sectionIndex, dataIndex] = [
          highlightedMarketIndex.sectionIndex,
          highlightedMarketIndex.dataIndex,
        ];

        // Handle UP, Decrement data
        if (e.key === "ArrowUp" && showCommandModal) {
          e.preventDefault();
          const canPrevDataIndex = dataIndex - 1 >= 0;
          const canPrevSectionIndex = sectionIndex - 1 >= 0;

          if (canPrevDataIndex) {
            setHighlightedMarketIndex({
              sectionIndex,
              dataIndex: dataIndex - 1,
            });
          } else if (!canPrevDataIndex && canPrevSectionIndex) {
            setHighlightedMarketIndex({
              sectionIndex: sectionIndex - 1,
              dataIndex: sectionsData[sectionIndex - 1].data.length - 1,
            });
          }
        } else if (e.key === "ArrowDown" && showCommandModal) {
          e.preventDefault();
          // Handle DOWN, Increment data
          const canNextDataIndex =
            dataIndex + 1 < sectionsData[sectionIndex].data.length;
          const canNextSectionIndex = sectionIndex + 1 < sectionsData.length;

          if (canNextDataIndex) {
            setHighlightedMarketIndex({
              sectionIndex,
              dataIndex: dataIndex + 1,
            });
          } else if (!canNextDataIndex && canNextSectionIndex) {
            setHighlightedMarketIndex({
              sectionIndex: sectionIndex + 1,
              dataIndex: 0,
            });
          }
        }
      }

      // Handle enter
      if (showCommandModal && e.key === "Enter" && highlightedMarketIndex) {
        const [sectionIndex, dataIndex] = [
          highlightedMarketIndex.sectionIndex,
          highlightedMarketIndex.dataIndex,
        ];
        const { data, instrumentType } = sectionsData[sectionIndex];
        const { asset } = data[dataIndex];
        onRowClick(asset, instrumentType);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [highlightedMarketIndex, onRowClick, showCommandModal, sectionsData]);
}

// Reset modal states when closed
export function useResetOnClose(
  showCommandModal: boolean,
  setSearchText: React.Dispatch<React.SetStateAction<string>>,
  setHighlightedMarketIndex: React.Dispatch<
    React.SetStateAction<HighlightedMarketIndex>
  >
) {
  useEffect(() => {
    if (!showCommandModal) {
      setSearchText("");
      setHighlightedMarketIndex({
        sectionIndex: 0,
        dataIndex: 0,
      });
    }
  }, [showCommandModal]);
}

// Reload data when modal is opened, and data is stale
// This prevents spamming open/close modal from hitting the apis repeatedly
export function useReloadStaleData(
  showCommandModal: boolean,
  marketsSummaryLastUpdatedMillis: number,
  setMarketsSummaryLastUpdatedMillis: React.Dispatch<
    React.SetStateAction<number>
  >,
  mutateMarketSummary: () => void,
  mutateIndexHistories: () => void
) {
  useEffect(() => {
    if (showCommandModal) {
      const now = Date.now();
      const nextUpdateTime = marketsSummaryLastUpdatedMillis + swrUpdateMillis;
      if (now > nextUpdateTime) {
        mutateMarketSummary();
        mutateIndexHistories();
        setMarketsSummaryLastUpdatedMillis(now);
      }
    }
  }, [
    marketsSummaryLastUpdatedMillis,
    mutateIndexHistories,
    mutateMarketSummary,
    showCommandModal,
  ]);
}
